import type { PlaySessionStartResponse } from "../contracts/PlaySession";

/**
 * The play session a normal boot started (decisions 80, 81): the one object every player call shares. It gives the
 * settings, the spin, the settlement, the gamble and the history their bearer, and numbers the spins and settlements
 * from one counter. The boot hands it by constructor to those callers only, so the public calls (a replay, the
 * currency catalog, the launch context) cannot send it. The identifier lives in a private field: nothing else reads
 * it, and no log, object dump or `JSON.stringify` of the session shows it. Memory only, gone with the page.
 */
export class PlaySession {
  readonly #identifier: string;
  #nextSequenceNumber: number;
  /**
   * The numbered request still without an answer, by its URL and body, and whether it had been sent before (a repeat
   * after a lost answer, which the backend may already have claimed).
   */
  #open: { request: string; sequenceNumber: number; repeat: boolean } | null = null;

  public constructor(start: PlaySessionStartResponse) {
    this.#identifier = start.sessionIdentifier;
    this.#nextSequenceNumber = start.nextSequenceNumber;
  }

  /** The header every player call carries (the backend's ADR 0072). */
  public authorizationHeader(): Record<string, string> {
    return { Authorization: `Bearer ${this.#identifier}` };
  }

  /**
   * The number a spin or a settlement carries: the next one; or, for the request asked for again after it got no
   * answer (the engine's one settlement retry, decision 17), its own, so the backend answers it with the first one's
   * outcome, or takes it now if the first never arrived. A new number there would skip one: 409 `sequence_conflict`.
   */
  public numberFor(request: string): number {
    const open = this.#open;
    const repeat = open !== null && open.request === request;
    const sequenceNumber = repeat ? open.sequenceNumber : this.#nextSequenceNumber;
    this.#open = { request, sequenceNumber, repeat };
    this.#nextSequenceNumber = sequenceNumber + 1;
    return sequenceNumber;
  }

  /** The backend answered that request: its number is spent. */
  public answered(sequenceNumber: number): void {
    if (this.#open?.sequenceNumber === sequenceNumber) this.#open = null;
  }

  /**
   * The backend refused that request before claiming its number (a 429; the backend, 2026-10-20): nothing was done,
   * and the next request, whatever it is, takes the same number. A repeat stays open: its first attempt, whose answer
   * was lost, may have claimed the number.
   */
  public refusedBeforeClaim(sequenceNumber: number): void {
    const open = this.#open;
    if (open === null || open.sequenceNumber !== sequenceNumber || open.repeat) return;
    this.#open = null;
    this.#nextSequenceNumber = sequenceNumber;
  }
}
